import $ from 'jquery'
import _ from 'lodash'
import featureMetadata from '../feature-metadata'
import i18n from 'scoreboard/components/i18n'
import BootslideMenu from 'scoreboard/components/bootslide/bootslide-menu'
import ChartDesignerFeature from '../../model/chart-designer-feature'
import menuIconOption from 'scoreboard/components/bootslide/menu-icon-option'
import appDisplay from 'scoreboard/components/app-display'
import ConstantValueForm from './constant-value-form'
import SerieSelectorForm from './serie-selector-form'

/**
 * These types must match up with the [ChartDesignerFeatureType] enum
 * The options that aren't being used in the initial release are commented out.
 *
 * #36724 the parentType property denotes that the target is the same type as the
 * parent, but with preconfigured property values
 * the handler allows performing some async actions to gather more information before
 * creating the analytic. It must call `processSelection` when it is done
 */

const showLinesAndBandOptions = (chart, axis) => chart.hasBarSeries({axis}) || chart.hasBreakdowns()

const ANALYTIC_MENU_OPTIONS = [{
	key: 'charts.summarize',
	targets: [{
		type: 'bar-stack',
		label: 'charts.barStack'
	}, {
		type: 'area-stack',
		label: 'charts.areaStack'
	}, {
		type: 'constant-line',
		parentType: 'lines-and-bands',
		label: 'charts.constantLine',
		handler: (chart, axis, processSelection) => {
			const formView = new ConstantValueForm({
				model: chart,
				axis
			})
			formView.on('done', processSelection)
			appDisplay.dialog(formView)
		},
		enabled: showLinesAndBandOptions
	}, {
		type: 'average-line',
		parentType: 'lines-and-bands',
		label: 'charts.averageLine',
		enabled: showLinesAndBandOptions
	}, {
		type: 'maximum-line',
		parentType: 'lines-and-bands',
		label: 'charts.maximumLine',
		enabled: showLinesAndBandOptions
	}, {
		type: 'minimum-line',
		parentType: 'lines-and-bands',
		label: 'charts.minimumLine',
		enabled: showLinesAndBandOptions
	}, {
		type: 'goal-line',
		parentType: 'lines-and-bands',
		label: 'charts.goalLines',
		enabled: chart => chart.hasOnlyScorecardItemSources() && showLinesAndBandOptions
	}, {
		type: 'scoring-bands',
		icon: 'scoring-bands-light',
		parentType: 'lines-and-bands',
		label: 'charts.lightScoringBands',
		handler: (chart, axis, processSelection) =>
			createScoringBands(chart, axis, processSelection, 'light'),
		enabled: chart => chart.hasScorecardItemSources()
	}, {
		type: 'scoring-bands',
		icon: 'scoring-bands-dark',
		parentType: 'lines-and-bands',
		label: 'charts.darkScoringBands',
		handler: (chart, axis, processSelection) =>
			createScoringBands(chart, axis, processSelection, 'dark'),
		enabled: chart => chart.hasScorecardItemSources()
	}, {
	// 	type: '75th-percentile-band',
	// 	label: 'charts.75thPercentileBand'
	// }, {
	// 	type: 'median-quartile-bands',
	// 	label: 'charts.MedianQuartileBands'
	// }, {
		type: 'lines-and-bands',
		label: 'charts.feature.lines-and-bands.menu',
		enabled: showLinesAndBandOptions
		// }, {
	// 	type: 'box-plot',
	// 	label: 'charts.boxPlot'
	}]
}, {
	key: 'trend-line',
	targets: [{
		type: 'trend-line',
		label: 'charts.feature.trend-line'
	// }, {
	// 	type: 'smoothing-line',
	// 	label: 'charts.smoothingLine'
	// }, {
	// 	type: 'burn-down-line',
	// 	label: 'charts.burnDownLine'
	// }, {
	// 	type: 'standard-deviation-lines',
	// 	label: 'charts.standardDeviationLines'
	// }, {
	// 	type: 'standard-deviation-bands',
	// 	label: 'charts.standardDeviationBands'
	}]
}]

/**
 * @param {ChartDesigner} chart
 * @param {String} axis "left" or "right"
 * @param {Function} processSelection
 */
function createScoringBands(chart, axis, processSelection, shade) {
	const series = chart.getFeatures({ type: 'serie', axis })
		.filter(s => s.get('source').domainType === 'scorecard-item')
	if (_.isEmpty(series)) processSelection()

	const createScoringBands = (serie) => {
		const request = {
			serieId: serie.id,
			serieTempId: serie.get('tempId'),
			source: serie.get('source'),
			axis,
			shade
		}
		$.post('/api/charts/scoring-bands', JSON.stringify(request))
			.then(results => processSelection(results))
	}
	if (series.length == 1) {
		createScoringBands(series[0])
	} else {
		const formView = new SerieSelectorForm({ chart, axis })
		formView.on('done', serieIds => {
			const serie = chart.features.findByIds(serieIds)
			createScoringBands(serie)
		})
		appDisplay.dialog(formView)
	}
}

/**
 * @property {ChartDesigner} model
 */
const AddAnalyticsMenu = BootslideMenu.extend({
	initialize: function(opts={}) {
		BootslideMenu.prototype.initialize.apply(this, arguments)
		this.options = opts
		this.axis = opts.axis
	},

	menu() {
		let menuOptions = {
			label: i18n.get('charts.addAnalytics'),
			categories: [],
			layout: 'tiles'
		}

		for (const option of ANALYTIC_MENU_OPTIONS) {
			let category = {
				key: option.key,
				label: i18n.get(option.key),
				target: []
			}

			for (const target of option.targets) {
				const enabled = (target.enabled || (() => true))(this.model, this.axis)
				const onClick = enabled ? () => this.onClickEndpoint(target) : () => {}
				category.target.push({
					key: target.label,
					label: target.label,
					content: menuIconOption(target.icon || target.type, i18n.get(target.label)),
					target: onClick,
					classNames: enabled ? [] : ['is-disabled']
				})
			}

			menuOptions.categories.push(category)
		}

		return menuOptions
	},

	onClickEndpoint: function(target) {
		const processSelection = (results) => {
			const type = target.type
			const parentType = target.parentType
			let endpointType = type.split('-')[1]
			let feature = this.createAnalytic(type, endpointType, parentType, results)
			if (!_.isNil(feature)) {
				const axisFeatures = this.model.featuresByAxis(this.options.axis)
				this.model.addFeature(feature, null, axisFeatures, feature.get('sortOrder'))
				this.model.sortFeatures(this.options.axis, axisFeatures)
			}
			this.trigger('done')
		}

		if (_.isFunction(target.handler)) {
			// Perform additional tasks before creating the analytic
			target.handler(this.model, this.options.axis, processSelection)
		} else {
			// Simply create it
			processSelection()
		}
	},

	createAnalytic: function(type, endpointType, parentType, formResults) {
		let metadata = featureMetadata[type] || featureMetadata[endpointType]
		if (_.isNil(metadata)) {
			return
		}

		let constructor = metadata.model
		let defaultAttrs = metadata.defaultAttrs ?
			metadata.defaultAttrs(type, this.model, formResults, this.axis) : {}

		const sortOrder = type === 'scoring-bands' ?
			this.model.featuresByAxis(this.options.axis).length : 0

		const properties = _.extend({}, {
			type: parentType || type,
			tempId: ChartDesignerFeature.tempId(),
			axis: this.options.axis,
			sortOrder
		}, defaultAttrs)

		// The axis is required on all features, so make sure the children have
		// it set too
		_.forEach(properties.children, child => {
			child.axis = this.options.axis
			child.tempId = ChartDesignerFeature.tempId()
			child.tempParentId = properties.tempId
		})
		return new constructor(properties)
	}

})

export default AddAnalyticsMenu

AddAnalyticsMenu.__filename__ = 'charts/edit/analytics/add-analytics-menu.js'
